import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { LanguageStore } from '../../../services/api/language/language-store.service';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { ToggleComponent } from '../../../components/data-input/form-inputs/toggle/toggle.component';
import { DropDownComponent } from '../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { CorporateDesignStore } from '../../../services/api/corporate-design/corporate-design-store.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import {
  ILanguageForm,
  ILanguageFormWrapper,
} from '../../../entities/language-entity';
import { LanguageFormsFactoryService } from '../../../factory/language/language-forms-factory.service';
import { LangToOptionsPipe } from '../../../pipes/lang-to-options/lang-to-options.pipe';
import {
  DEFAULT_LANGUAGES,
  ILanguage,
  INormalText,
  LanguagePrefix,
} from '@web/dtos';
import { map, Observable, Subscription } from 'rxjs';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { RevealFormErrorsButtonComponent } from '../../../components/feedback/reveal-form-errors-button/reveal-form-errors-button.component';
import { AddLanguageDialogComponent } from '../../../components/dialogs/add-language-dialog/add-language-dialog.component';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

interface IStandardLanguageForm {
  standardLanguage: FormControl<{ value: string; name: string }>;
}

@Component({
  selector: 'aw-languages-page',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    SectionComponent,
    SectionHeaderActionsComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    LoadingAnimationComponent,
    ToggleComponent,
    DropDownComponent,
    LangToOptionsPipe,
    RevealFormErrorsButtonComponent,
    AppendTestIdPipe,
  ],
  templateUrl: './language-page.component.html',
  styles: ``,
})
export class LanguagePageComponent
  extends FormTracking<ILanguageFormWrapper>
  implements OnDestroy
{
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  readonly languageStore: LanguageStore = inject(LanguageStore);
  readonly textStore: WebTextStore = inject(WebTextStore);
  readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  readonly languageFactory: LanguageFormsFactoryService = inject(
    LanguageFormsFactoryService
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly langToOptionsPipe: LangToOptionsPipe =
    inject(LangToOptionsPipe);
  public standardLanguageForm: FormGroup<IStandardLanguageForm> = new FormGroup(
    {
      standardLanguage: this.formBuilder.nonNullable.control<{
        value: string;
        name: string;
      }>(this.getStandardLanguage(), []),
    }
  );
  public languageForm: FormGroup<ILanguageFormWrapper> =
    this.languageFactory.buildForm(this.languageStore.languages());

  private readonly subscription: Subscription = new Subscription();

  constructor() {
    super();
    effect((): void => {
      this.languageForm = this.languageFactory.buildForm(
        this.languageStore.languages()
      );
      this.standardLanguageForm.setValue({
        standardLanguage: this.getStandardLanguage(),
      });
      this.initFormTracking(this.languageForm);
      this.createActivitySubscription();
    });
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.languageForm.dirty) {
        event.preventDefault();
      }
    };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  save(): boolean {
    if (!this.languageForm.valid || !this.standardLanguageForm.valid) {
      this.languageForm.markAllAsTouched();
      this.standardLanguageForm.markAllAsTouched();
      return false;
    }

    this.languagesToBeDeleted.forEach(id => {
      this.languageStore.deleteLanguage(id);
    });
    this.languagesToBeDeleted = [];

    this.languageStore.save(
      this.languageFactory.buildEntity(this.languageForm)
    );
    return true;
  }

  percentage(prefix: string): string {
    const deAmount: number = this.textStore
      .texts()
      .filter(
        (t: INormalText) =>
          t.text.values.filter(val => val.languagePrefix === 'DE').length > 0
      ).length;
    const langAmount: number = this.textStore
      .texts()
      .filter(
        (t: INormalText) =>
          t.text.values.filter(val => val.languagePrefix === prefix).length > 0
      ).length;
    return (langAmount / deAmount) * 100 + '%';
  }

  getLanguageControl(languageId: string): FormGroup {
    return this.languageForm.controls.languages.controls.find(
      lang => lang.controls.id.value === languageId
    ) as FormGroup;
  }

  createActivitySubscription(): void {
    this.subscription.add(
      this.standardLanguageForm.controls.standardLanguage.valueChanges.subscribe(
        standardLanguage => {
          if (standardLanguage) {
            // Reset all 'isStandard' values to false first
            (
              this.languageForm.controls.languages as FormArray
            ).controls.forEach((control: AbstractControl) => {
              const group = control as FormGroup;
              group.controls['isStandard'].setValue(false);
            });

            // Set the selected language as the standard one
            const selectedGroup = this.getLanguageControl(
              standardLanguage.value
            );
            if (selectedGroup) {
              selectedGroup.controls['isStandard'].setValue(true);
            }
          }
        }
      )
    );
  }

  resetFormChanges() {
    this.standardLanguageForm.reset({
      standardLanguage: this.getStandardLanguage(),
    });
    this.languageForm.controls.languages.clear();
    this.languageStore.languages().forEach((language: ILanguage) => {
      this.languageForm.controls.languages.push(
        this.languageFactory.buildLanguageFormGroup(language)
      );
    });
    this.languageForm.updateValueAndValidity();
    this.markAsUntouched();
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<{
      icon: string;
      iconBackgroundColor: string;
    }>(ResetChangesDialogComponent, {
      headline: this.cmsTextProvider.shared.revertChanges,
      width: '600px',
      icon: 'warning',
      iconBackgroundColor: '#eed202',
    });
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  public handleAddLanguageDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<{
      name: string;
      prefix: LanguagePrefix;
    }>(AddLanguageDialogComponent, {
      headline: 'Neue Übersetzungssprache hinzufügen',
      width: '600px',
      name: '',
      prefix: '' as LanguagePrefix,
    });
    dialogRef.closed.subscribe(result => {
      if (
        result != undefined &&
        result.submit &&
        result.data.name !== undefined &&
        result.data.prefix !== undefined
      ) {
        this.languageForm.controls.languages.controls.push(
          this.languageFactory.buildLanguageFormGroup({
            name: result.data.name,
            prefix: result.data.prefix,
            isActive: false,
            isStandard: false,
          })
        );
        this.cdRef.detectChanges();
        this.languageForm.markAsDirty();
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  public handleEditLanguageDialog(index: number): void {
    const languageControl = this.languageForm.controls.languages.at(index);
    const currentLanguage = languageControl.value;

    const dialogRef = this.dialogService.openDialog<{
      name: string;
      prefix: LanguagePrefix;
    }>(AddLanguageDialogComponent, {
      headline: 'Sprache bearbeiten',
      width: '600px',
      name: currentLanguage.name || '',
      prefix: currentLanguage.prefix || ('' as LanguagePrefix),
    });

    dialogRef.closed.subscribe(result => {
      if (
        result != undefined &&
        result.submit &&
        result.data.name !== undefined &&
        result.data.prefix !== undefined
      ) {
        languageControl.patchValue({
          name: result.data.name,
          prefix: result.data.prefix,
        });
        this.cdRef.detectChanges();
        this.languageForm.markAsDirty();
      }
    });
  }

  public languagesToBeDeleted: string[] = [];

  public handleDeleteLanguage(index: number): void {
    const languageControl: FormGroup<ILanguageForm> =
      this.languageForm.controls.languages.at(index);
    const languageToDelete = languageControl.value;
    if (languageToDelete.isStandard) return;

    if (languageToDelete.id) {
      this.languagesToBeDeleted.push(languageToDelete.id);
    }
    this.languageForm.controls.languages.removeAt(index);
    this.cdRef.detectChanges();
    this.languageForm.markAsDirty();
  }

  private markAsUntouched(): void {
    this.languageForm.markAsUntouched();
    this.standardLanguageForm.markAsUntouched();
  }

  private getStandardLanguage(): { value: string; name: string } {
    return (
      this.langToOptionsPipe.transform(
        this.languageStore
          .languages()
          .filter((lang: ILanguage): boolean => lang.isStandard)
      )[0] || DEFAULT_LANGUAGES[0]
    );
  }
}
